// Heartbeat panel for one persistent-agent thread — attach / edit / pause /
// remove the thread's recurring wake schedules. A heartbeat delivers its
// prompt as a `wake` signal (when-idle) on a cron, so an idle thread wakes
// and a busy one sees it after its current turn. A thread may have several
// heartbeats (capped at 20), each optionally labeled to tell them apart.
import { useClient } from '@cortex/react-query';
import type { PersistentAgentHeartbeat } from '@cortex/types';
import { fork } from '@cortex/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { formatDateTime, formatRelative } from '../../lib/format';

const defaultPrompt = 'Heartbeat: review your facts and objective, and take any pending actions.';
const maxHeartbeatsPerAgent = 20;

// Same high-utility presets as the refinement schedules page — most-frequent
// first. Times resolve in the chosen timezone.
const cronPresets: { label: string; expr: string }[] = [
  { label: 'Every 5 min', expr: '*/5 * * * *' },
  { label: 'Every 15 min', expr: '*/15 * * * *' },
  { label: 'Every 30 min', expr: '*/30 * * * *' },
  { label: 'Hourly', expr: '0 * * * *' },
  { label: 'Every 6 hours', expr: '0 */6 * * *' },
  { label: 'Daily 09:00', expr: '0 9 * * *' },
];

function getTimezones(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
      .supportedValuesOf;
    if (typeof fn === 'function') return fn('timeZone');
  } catch {
    // fall through
  }
  return ['UTC', 'America/Los_Angeles', 'America/New_York', 'Europe/London', 'Asia/Tokyo'];
}

const timezones = getTimezones();

function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** One heartbeat's display row: rate/next-fire/last-fired + pause/resume/edit/remove. */
function HeartbeatRow({
  base,
  heartbeat,
  onEdit,
  onChanged,
}: {
  base: string;
  heartbeat: PersistentAgentHeartbeat;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const client = useClient();
  const url = `${base}/${heartbeat.id}`;

  const setStatus = useMutation({
    mutationFn: (next: 'ACTIVE' | 'PAUSED') =>
      client.http.patch(url, { json: { status: next } }).json<PersistentAgentHeartbeat>(),
    onSuccess: onChanged,
  });

  const remove = useMutation({
    mutationFn: () => client.http.delete(url).text(),
    onSuccess: onChanged,
  });

  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--border-color, #333)' }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <span className="t-sm" style={{ fontWeight: 600 }}>
          {heartbeat.label ?? <code>{heartbeat.cron}</code>}
        </span>
        {heartbeat.label && (
          <span className="t-sm">
            <code>{heartbeat.cron}</code>
            <span className="t-xs t-muted"> ({heartbeat.timezone ?? 'UTC'})</span>
          </span>
        )}
        {!heartbeat.label && <span className="t-xs t-muted">({heartbeat.timezone ?? 'UTC'})</span>}
        <span
          className={
            heartbeat.status === 'ACTIVE' ? 'badge badge--success' : 'badge badge--warning'
          }
        >
          {heartbeat.status === 'ACTIVE' ? 'active' : 'paused'}
        </span>
        <span className="t-sm" title={formatDateTime(heartbeat.nextFireAt)}>
          <span className="t-muted">Next fire </span>
          {heartbeat.status === 'ACTIVE' ? (
            <>
              {formatRelative(heartbeat.nextFireAt)}
              <span className="t-xs t-muted"> · {formatDateTime(heartbeat.nextFireAt)}</span>
            </>
          ) : (
            <span className="t-muted">paused</span>
          )}
        </span>
        <span className="t-sm">
          <span className="t-muted">Last fired </span>
          {formatRelative(heartbeat.lastFiredAt)}
        </span>
      </div>
      <p className="t-sm" style={{ margin: '8px 0 0', color: 'var(--text-secondary)' }}>
        “{heartbeat.prompt}”
      </p>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        {heartbeat.status === 'ACTIVE' ? (
          <button
            className="btn btn--ghost btn--sm"
            disabled={setStatus.isPending}
            title="Keep the schedule but stop firing until resumed"
            onClick={() => setStatus.mutate('PAUSED')}
          >
            Pause
          </button>
        ) : (
          <button
            className="btn btn--ghost btn--sm"
            disabled={setStatus.isPending}
            title="Resume — the next fire is recomputed from now (missed beats are not replayed)"
            onClick={() => setStatus.mutate('ACTIVE')}
          >
            Resume
          </button>
        )}
        <button className="btn btn--ghost btn--sm" onClick={onEdit}>
          Edit
        </button>
        <button
          className="btn btn--destructive btn--sm"
          disabled={remove.isPending}
          title="Remove this heartbeat"
          onClick={() => remove.mutate()}
          style={{ marginLeft: 'auto' }}
        >
          Remove
        </button>
      </div>
      {(setStatus.isError || remove.isError) && (
        <div className="alert alert--danger" style={{ marginTop: 8 }}>
          {String(((setStatus.error || remove.error) as Error)?.message ?? 'Update failed')}
        </div>
      )}
    </div>
  );
}

/** Create-or-edit form for one heartbeat. Creates when `existing` is absent. */
function HeartbeatForm({
  base,
  existing,
  onDone,
  onChanged,
}: {
  base: string;
  existing: PersistentAgentHeartbeat | null;
  onDone: () => void;
  onChanged: () => void;
}) {
  const client = useClient();
  const [label, setLabel] = useState(existing?.label ?? '');
  const [cron, setCron] = useState(existing?.cron ?? '*/30 * * * *');
  const [timezone, setTimezone] = useState(existing?.timezone ?? getBrowserTimezone());
  const [prompt, setPrompt] = useState(existing?.prompt ?? defaultPrompt);

  const save = useMutation({
    mutationFn: () => {
      const json = {
        label: label.trim() || null,
        cron: cron.trim(),
        timezone,
        prompt: prompt.trim(),
      };
      return existing
        ? client.http.patch(`${base}/${existing.id}`, { json }).json<PersistentAgentHeartbeat>()
        : client.http.post(base, { json }).json<PersistentAgentHeartbeat>();
    },
    onSuccess: () => {
      onChanged();
      onDone();
    },
  });

  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--border-color, #333)' }}>
      <label className="field" style={{ marginBottom: 8 }}>
        <span className="field-label">Label (optional) — helps tell heartbeats apart</span>
        <input
          className="input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Daily standup nudge"
        />
      </label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {cronPresets.map((p) => (
          <button
            key={p.expr}
            className={`btn btn--sm ${cron === p.expr ? 'btn--secondary' : 'btn--ghost'}`}
            onClick={() => setCron(p.expr)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <label className="field" style={{ flex: '0 0 200px' }}>
          <span className="field-label">Cron</span>
          <input
            className="input t-mono"
            value={cron}
            onChange={(e) => setCron(e.target.value)}
            placeholder="*/30 * * * *"
          />
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span className="field-label">Timezone</span>
          <select
            className="select-native"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {timezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span className="field-label">Prompt — what each beat tells the thread</span>
        <textarea
          className="input"
          rows={2}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          style={{ resize: 'vertical' }}
        />
      </label>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button
          className="btn btn--primary btn--sm"
          disabled={!cron.trim() || !prompt.trim() || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : existing ? 'Save heartbeat' : 'Add heartbeat'}
        </button>
        <button className="btn btn--ghost btn--sm" onClick={onDone}>
          Cancel
        </button>
      </div>
      {save.isError && (
        <div className="alert alert--danger" style={{ marginTop: 8 }}>
          {String((save.error as Error)?.message ?? 'Failed to save heartbeat')}
        </div>
      )}
    </div>
  );
}

export function HeartbeatPanel({
  agentId,
  threadId,
  heartbeats,
  status,
}: {
  agentId: string;
  threadId: string;
  heartbeats: PersistentAgentHeartbeat[];
  status: string | null;
}) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);

  const base = `v1/persistent-agent-groups/${agentId}/agents/${threadId}/heartbeats`;
  const onChanged = () => {
    fork(qc.invalidateQueries({ queryKey: ['persistent-agent-session', threadId] }));
    fork(qc.invalidateQueries({ queryKey: ['persistent-agents', agentId, 'agents'] }));
  };

  const terminal = status === 'STOPPED' || status === 'FAILED';
  const atCap = heartbeats.length >= maxHeartbeatsPerAgent;

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="card-title">Heartbeats</span>
        {heartbeats.length > 0 && <span className="badge">{heartbeats.length}</span>}
      </div>
      <div className="card-body">
        {terminal && heartbeats.length === 0 ? (
          <span className="t-muted t-sm">This thread is stopped — no heartbeats to add.</span>
        ) : (
          <>
            {heartbeats.length === 0 && !addingNew && (
              <span className="t-sm t-muted">
                No heartbeats — this thread only wakes when a message or signal arrives.
              </span>
            )}
            {heartbeats.map((hb) =>
              editingId === hb.id ? (
                <HeartbeatForm
                  key={hb.id}
                  base={base}
                  existing={hb}
                  onDone={() => setEditingId(null)}
                  onChanged={onChanged}
                />
              ) : (
                <HeartbeatRow
                  key={hb.id}
                  base={base}
                  heartbeat={hb}
                  onEdit={() => setEditingId(hb.id)}
                  onChanged={onChanged}
                />
              ),
            )}
            {addingNew && (
              <HeartbeatForm
                base={base}
                existing={null}
                onDone={() => setAddingNew(false)}
                onChanged={onChanged}
              />
            )}
            {!addingNew && !terminal && (
              <div style={{ marginTop: heartbeats.length > 0 ? 12 : 8 }}>
                <button
                  className="btn btn--secondary btn--sm"
                  disabled={atCap}
                  title={atCap ? `Max ${maxHeartbeatsPerAgent} heartbeats` : undefined}
                  onClick={() => setAddingNew(true)}
                >
                  Add heartbeat…
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
